/**
 * Business plane provisioning gate — block ERP paths until intake ready or skipped (P4)
 *
 * SSOT: TENANT_PROVISIONING_ARCHITECTURE.md §2.1 · GENESIS_IDENTITY_TENANT_SPLIT_GAP P4
 */

import { isBusinessPlanePath } from './pathPlanes';

export type BusinessPlaneProvisioningStatus =
  | 'draft'
  | 'provisioning'
  | 'provisioned'
  | 'intake_pending'
  | 'ready'
  | 'failed';

export interface BusinessPlaneProvisioningContext {
  provisioningStatus?: BusinessPlaneProvisioningStatus | null;
  tenantsStatus?: 'active' | 'inactive' | 'suspended';
  intakeSkipped?: boolean;
  genesisCompletedAt?: string | null;
}

export type BusinessPlaneProvisioningBlockReason =
  | 'legacy_allow'
  | 'ready'
  | 'intake_skipped'
  | 'workspace_provisioned_intake'
  | 'provisioning_blocked';

export interface BusinessPlaneProvisioningDecision {
  allowed: boolean;
  reason: BusinessPlaneProvisioningBlockReason;
}

function normalizePathname(pathname: string): string {
  if (!pathname || pathname === '/') return '/';
  const withoutTrailing = pathname.replace(/\/+$/, '') || '/';
  return withoutTrailing.startsWith('/') ? withoutTrailing : `/${withoutTrailing}`;
}

export function isWorkspaceBusinessPath(pathname: string): boolean {
  const path = normalizePathname(pathname);
  return path === '/workspace' || path.startsWith('/workspace/');
}

const PROVISIONED_OR_BEYOND = new Set<BusinessPlaneProvisioningStatus>([
  'provisioned',
  'intake_pending',
  'ready',
]);

function isProvisionedOrBeyond(
  status: BusinessPlaneProvisioningStatus | null | undefined
): boolean {
  return status !== null && status !== undefined && PROVISIONED_OR_BEYOND.has(status);
}

function isLegacyActiveTenant(context: BusinessPlaneProvisioningContext): boolean {
  return (
    (context.provisioningStatus === null || context.provisioningStatus === undefined) &&
    context.tenantsStatus === 'active' &&
    context.intakeSkipped !== true &&
    !context.genesisCompletedAt
  );
}

export function resolveBusinessPlaneProvisioningDecision(
  pathname: string,
  context: BusinessPlaneProvisioningContext
): BusinessPlaneProvisioningDecision {
  if (!isBusinessPlanePath(pathname)) {
    return { allowed: true, reason: 'legacy_allow' };
  }

  if (isLegacyActiveTenant(context)) {
    return { allowed: true, reason: 'legacy_allow' };
  }

  if (context.intakeSkipped === true) {
    return { allowed: true, reason: 'intake_skipped' };
  }

  if (context.provisioningStatus === 'ready') {
    return { allowed: true, reason: 'ready' };
  }

  if (isWorkspaceBusinessPath(pathname)) {
    const intakeComplete = Boolean(context.genesisCompletedAt);
    if (isProvisionedOrBeyond(context.provisioningStatus) && intakeComplete) {
      return { allowed: true, reason: 'workspace_provisioned_intake' };
    }
  }

  return { allowed: false, reason: 'provisioning_blocked' };
}

export function canAccessBusinessPlanePath(
  pathname: string,
  context: BusinessPlaneProvisioningContext
): boolean {
  return resolveBusinessPlaneProvisioningDecision(pathname, context).allowed;
}

export function buildGenesisIntakeRedirectPath(tenantId: string): string {
  return `/genesis/intake?tenantId=${encodeURIComponent(tenantId.trim())}`;
}
